import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { PERMISSION_MODULE_GROUPS } from '../utils/permissions';

/**
 * Mobile App Home — the entry screen rendered inside the Capacitor
 * Android/iOS shell (and also accessible from any browser via /m/home).
 *
 * Layout = big tap-target grid of the modules the company admin curated
 * under Company Settings → 📱 Mobile App Menu, intersected with the
 * current user's view-permission (the backend already does the filtering).
 *
 * Design rules:
 *  • Big finger-friendly tiles (min 96px tall) with icon + label.
 *  • One column on tight phones, two columns on wider phones / tablets.
 *  • Header shows the user's name + company; bottom bar has Refresh +
 *    Sign-out for parity with the web sidebar.
 *  • Visiting any tile uses the same /<route> the web does — no parallel
 *    universe of mobile-only screens. Same auth, same data.
 */

// Map a permission-module key → its web route. The mobile shell deep-links
// to these same routes; the user lands on the existing module page.
const ROUTE_FOR = {
  dashboard: '/dashboard',
  activities: '/activities',
  customers: '/customers',
  products: '/products',
  leads: '/leads',
  sales_invoices: '/sales-invoices',
  delivery_notes: '/delivery-notes',
  service_requests: '/service-requests',
  payments: '/payments',
  supplier_payments: '/supplier-payments',
  sales_reports: '/sales-reports',
  suppliers: '/suppliers',
  purchase_orders: '/purchase-orders',
  purchase_entries: '/purchase-entries',
  vat_return: '/vat-return',
  other_purchases: '/other-purchases',
  daily_tasks: '/daily-tasks',
  attendance: '/attendance',
  attendance_report: '/attendance-report',
  users: '/users',
  teams: '/teams',
  master_data: '/master-data',
  reports: '/reports',
  stock_report: '/stock-report',
  company_settings: '/company-settings',
  bulk_delete: '/bulk-delete',
  location_tracking: '/location-tracking',
  approval_settings: '/approval-settings',
};

const MODULE_INDEX = Object.fromEntries(
  PERMISSION_MODULE_GROUPS.flatMap((g) => g.modules).map((m) => [m.key, m]),
);

const MobileHome = () => {
  const { user, logout } = useAuth();
  const { companySettings } = useCurrency();
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/companies/mobile-menu');
        if (cancelled) return;
        setKeys(res.data?.keys || []);
        setCompanyName(res.data?.company_name || '');
      } catch {
        if (!cancelled) setKeys([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex flex-col" data-testid="mobile-home">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white px-4 pt-6 pb-4 shadow-md">
        <div className="text-xs opacity-80">{companyName || companySettings?.name || 'CRM'}</div>
        <div className="flex items-baseline justify-between mt-1">
          <h1 className="text-2xl font-bold" data-testid="mobile-home-greeting">
            Hi, {user?.full_name?.split(' ')[0] || user?.email || 'there'} 👋
          </h1>
          {companySettings?.logo_url && (
            <img src={companySettings.logo_url} alt="" className="h-8 max-w-[80px] object-contain bg-white/10 rounded p-1" />
          )}
        </div>
        <div className="text-[11px] opacity-80 mt-1">
          {new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
        </div>
      </header>

      {/* Tile grid */}
      <main className="flex-1 p-4">
        {loading && (
          <div className="text-center text-sm text-slate-500 pt-12">Loading…</div>
        )}
        {!loading && keys.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
            <div className="text-3xl mb-2">📱</div>
            <div className="font-semibold text-amber-900">No mobile modules enabled</div>
            <p className="text-xs text-amber-700 mt-1">
              Ask your admin to curate the Mobile App Menu in <b>Company Settings → 📱 Mobile App Menu</b>.
            </p>
          </div>
        )}
        {!loading && keys.length > 0 && (
          <div className="grid grid-cols-2 gap-3" data-testid="mobile-home-tiles">
            {keys.map((k) => {
              const m = MODULE_INDEX[k];
              const route = ROUTE_FOR[k];
              if (!m || !route) return null;
              return (
                <Link
                  key={k}
                  to={route}
                  className="bg-white rounded-2xl shadow-sm hover:shadow-md border border-slate-200 active:bg-slate-50 active:scale-[0.98] transition-all p-4 flex flex-col items-center justify-center text-center min-h-[112px]"
                  data-testid={`mobile-tile-${k}`}
                >
                  <div className="text-3xl mb-2">{m.icon}</div>
                  <div className="text-sm font-medium text-slate-800 leading-tight">{m.label}</div>
                </Link>
              );
            })}
          </div>
        )}
      </main>

      {/* Bottom bar */}
      <nav className="bg-white border-t border-slate-200 px-4 py-2 flex justify-around items-center sticky bottom-0" data-testid="mobile-bottom-nav">
        <Link to="/m/home" className="flex flex-col items-center text-xs text-indigo-700 font-medium" data-testid="mobile-nav-home">
          <span className="text-xl">🏠</span> Home
        </Link>
        <Link to="/attendance" className="flex flex-col items-center text-xs text-slate-600" data-testid="mobile-nav-punch">
          <span className="text-xl">🕒</span> Punch
        </Link>
        <Link to="/leads" className="flex flex-col items-center text-xs text-slate-600" data-testid="mobile-nav-lead">
          <span className="text-xl">🎯</span> Lead
        </Link>
        <button onClick={handleLogout} className="flex flex-col items-center text-xs text-rose-600" data-testid="mobile-nav-logout">
          <span className="text-xl">↪</span> Sign out
        </button>
      </nav>

      {/* Build-version footer — lets the user instantly tell whether the
          APK / WebView is running the latest deploy. The timestamp is
          baked into the JS bundle at craco build/start time, so a fresh
          rebuild always shows a new value. If the user sees an OLD value
          after a "fresh" install, they are looking at a cached bundle. */}
      <div className="text-[10px] text-center text-slate-400 py-1 bg-slate-50 border-t border-slate-100" data-testid="mobile-build-stamp">
        Build {process.env.REACT_APP_BUILD_TIME || 'dev'}
      </div>
    </div>
  );
};

export default MobileHome;
